<template>
  <div class="container reviews-section">
    <div class="row mb-24 pt-56 pt-lg-80 pb-40 pb-lg-72">
      <div class="col-md-10 offset-md-1 pt-lg-16">
        <h2 class="text-center mb-40 mb-lg-46">
          {{ $label('books.details.reviewsTitle') }}
        </h2>
        <div v-if="reviewSubmitted" class="mb-32">
          <SuccessAlert>
            <span class="fw-semibold">
              {{ $label('books.reviews.successAlertTitle') }}
            </span>
            <p class="mb-0 mt-8">
              {{ $label('books.reviews.successAlertDescription') }}
            </p>
          </SuccessAlert>
        </div>

        <div v-else-if="userAlreadyWroteReview" class="mb-32">
          <WarningAlert>
            <span class="fw-semibold">
              {{ $label('books.reviews.alreadyWroteReviewAlertTitle') }}
            </span>
            <p class="mb-0 mt-8">
              {{ $label('books.reviews.alreadyWroteReviewAlertDescription') }}
            </p>
          </WarningAlert>
        </div>

        <div
          v-if="initialReviewsLoaded"
          ref="starsRating"
          class="mb-24 d-flex justify-content-between flex-wrap flex-gap-8"
        >
          <div class="rating-total d-flex align-items-center">
            <StarsRating :rating="averageRating" />
            <small class="text-small">
              {{ bookRatingsLabel }}
            </small>
          </div>

          <BaseSelect
            v-if="reviews.length"
            :options="sortingOptions"
            :select-label="$label('page.shop.sorting.sort')"
            :on-change="onSortOptionClicked"
            custom-width="124px"
          />
        </div>

        <p
          v-if="!isLoadingReviews && initialReviewsLoaded && !reviews.length"
          class="mb-24"
        >
          {{ $label('books.reviews.noReviews') }}
        </p>

        <BaseButton
          v-if="!loggedInUser"
          variant="primary"
          class="login-btn"
          data-testid="login-btn"
          @click="openLoginDrawer"
        >
          {{ $label('books.reviews.login') }}
        </BaseButton>

        <WriteReviewModal
          v-else-if="
            !reviewSubmitted && !userAlreadyWroteReview && initialReviewsLoaded
          "
          :logged-in-user="loggedInUser"
          :book-slug="book.slug"
          :book-uid="book.id"
        />

        <div v-if="isLoadingReviews" class="mb-40">
          <ReviewSkeleton v-for="index in 2" :key="index" />
        </div>

        <ReviewsList
          v-else-if="reviews.length"
          :reviews="reviews"
          :book-title="book.title"
          :report-review="reviewsStore.reportReview"
          :logged-in-user="loggedInUser"
          :open-login-drawer="openLoginDrawer"
          :like-review="reviewsStore.likeReview"
          :remove-like="reviewsStore.removeLike"
          :delete-review="reviewsStore.deleteReview"
          :update-reviews="requestReviews"
        />

        <div
          v-if="totalPages > 1"
          class="mt-64 mt-md-32 d-flex justify-content-center"
        >
          <Pagination
            :total-pages="totalPages"
            :current-page="currentPage"
            @page-changed="pageChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '~/stores/user'
import { useReviewsStore } from '~/stores/reviews'
import { isElementInViewport } from '~/utils/index'
import SuccessAlert from '~/components/alerts/SuccessAlert'
import WarningAlert from '~/components/alerts/WarningAlert'
import WriteReviewModal from '~/components/reviews/WriteReviewModal'
import ReviewsList from '~/components/reviews/ReviewsList'
import StarsRating from '~/components/reviews/StarsRating'
import ReviewSkeleton from '~/components/reviews/ReviewSkeleton'
import BaseSelect from '~/components/BaseSelect'
import Pagination from '~/components/pagination/Pagination'
import { useReviews } from '~/composables/useReviews'
import { useDrawer } from '~/composables/useDrawer'

export default {
  components: {
    SuccessAlert,
    WarningAlert,
    WriteReviewModal,
    ReviewsList,
    StarsRating,
    ReviewSkeleton,
    BaseSelect,
    Pagination
  },
  props: {
    book: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const book = toRef(props, 'book')
    const { averageRating, numberOfRatings, bookRatingsLabel } = useReviews(
      book.value
    )
    const { updateDrawerState } = useDrawer()
    const reviewsStore = useReviewsStore()
    const userStore = useUserStore()

    return {
      averageRating,
      numberOfRatings,
      bookRatingsLabel,
      updateDrawerState,
      reviewsStore,
      userStore
    }
  },
  data() {
    return {
      isLoadingReviews: true,
      initialReviewsLoaded: false,
      sortingOptions: [
        {
          id: 'CreateDate_Desc',
          label: this.$label('books.reviews.sorting.dateDesc')
        },
        {
          id: 'CreateDate_Asc',
          label: this.$label('books.reviews.sorting.dateAsc')
        },
        {
          id: 'Rating_Desc',
          label: this.$label('books.reviews.sorting.ratingDesc')
        },
        {
          id: 'Rating_Asc',
          label: this.$label('books.reviews.sorting.ratingAsc')
        },
        {
          id: 'TotalHeartReactions_Desc',
          label: this.$label('books.reviews.sorting.likesDesc')
        }
      ]
    }
  },
  computed: {
    loggedInUser() {
      return this.userStore.loggedInUser
    },
    reviews() {
      return this.reviewsStore.items
    },
    reviewSubmitted() {
      return this.reviewsStore.reviewSubmitted
    },
    totalPages() {
      return this.reviewsStore.totalPages
    },
    currentPage() {
      return this.reviewsStore.currentPage
    },
    sort() {
      return this.reviewsStore.sort
    },
    sortDirection() {
      return this.reviewsStore.sortDirection
    },
    userAlreadyWroteReview() {
      return this.reviewsStore.userAlreadyWroteReview
    }
  },
  watch: {
    reviewSubmitted(submitted) {
      if (submitted) {
        this.requestReviews({
          pageIndex: 1,
          sort: 'CreateDate',
          sortDirection: 'Desc'
        })
      }
    },
    async loggedInUser(newUser, oldUser) {
      const shouldUpdateReviews =
        this.initialReviewsLoaded && newUser?.id !== oldUser?.id

      if (shouldUpdateReviews) {
        await this.loadReviews()
      }
    }
  },
  mounted() {
    this.loadReviews()
  },
  methods: {
    openLoginDrawer() {
      const loginMenu = {
        menuPositionClass: 'right',
        title: 'user.signIn.title',
        component: 'base-login',
        props: {
          isHeadingShown: false,
          loginFormPrefix: 'login-',
          shouldPreventRedirect: true,
          inSlideMenu: true
        }
      }

      this.updateDrawerState({
        drawerOptions: loginMenu,
        isDrawerShown: true
      })
    },
    async loadReviews() {
      if (!this.numberOfRatings) {
        this.initialReviewsLoaded = true
        this.isLoadingReviews = false
      } else {
        await this.requestReviews({ pageIndex: 1 })
      }
    },
    async requestReviews(params) {
      try {
        this.isLoadingReviews = true

        await this.reviewsStore.getReviews({
          bookSlug: this.book.slug,
          sort: this.sort,
          sortDirection: this.sortDirection,
          pageIndex: this.currentPage,
          pageSize: 2,
          ...params
        })
      } catch (error) {
        this.$toast.error(this.$label('messages.errorOccuredMessage'))
        this.$sentryCaptureException(error)
      } finally {
        this.isLoadingReviews = false
        this.initialReviewsLoaded = true
      }
    },
    async onSortOptionClicked(option) {
      this.sortParams = option.split('_')

      await this.requestReviews({
        sort: this.sortParams[0],
        sortDirection: this.sortParams[1],
        pageIndex: 1
      })
    },
    async pageChanged(page) {
      await this.requestReviews({
        pageIndex: page
      })

      this.$nextTick(this.scrollReviewsIntoViewport)
    },
    scrollReviewsIntoViewport() {
      try {
        const { starsRating } = this.$refs

        if (!isElementInViewport(starsRating)) {
          starsRating.scrollIntoView({
            behavior: 'smooth'
          })
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.review:last-child) {
  hr {
    display: none;
  }
}

.rating-total {
  max-width: 120px;
  flex-wrap: wrap;
  gap: 8px;
}

@include media-breakpoint-up(md) {
  .rating-total {
    max-width: unset;
    gap: 16px;
  }
}
</style>
